import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import styled, { css, CSSProperties } from 'styled-components';
import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { TableContext } from '../../contexts/TableContext';
import { DASHBOARD_NAV_WIDTH } from '../../layouts/ViewLayout';
import { DashboardContext } from '../../scenes/DashboardScene/DashboardContext';
import Alert from '../base/Alert';
import Button from '../base/Button';
import Divider from '../base/Divider';
import Icon, { IconName, StyledIcon } from '../base/Icon';
import Loading from '../base/Loading';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import { EmptyResults, StyledEmptyStateWrapper } from '../EmptyState';
import { Div, StyledUtilsProps } from '../helpers/StyledUtils';
import DraggableColumnHeader from './DraggableColumnHeader';
import TableFooter from './TableFooter';
type ColumnWidth = number | {
  max?: number;
  min?: number;
  type?: 'percentage' | 'px';
};
const pagination_height = 44;
const selected_outline_width = 1;

// Helper functions for offset calculations
const calculate_sticky_offset = {
  rowWithinTable: (base_offset: number) => base_offset + selected_outline_width
};
const StyledHeaderWrapper = styled.div<{
  full_page: boolean;
  nested?: boolean;
  top_offset: number;
}>(({
  full_page,
  nested,
  top_offset,
  theme
}) => {
  return css`
    position: relative;
    ${full_page && !nested && css`
      position: sticky;
      top: ${theme.pxToRem(top_offset || 0)};
      z-index: ${nested ? 3 : 4};
    `}
  `;
});
const StyledNestedBlock = styled.div<{
  full_page: boolean;
  top_offset: number;
  nested?: boolean;
}>(({
  theme
}) => {
  return css`
    position: relative;
    border-bottom: ${theme.border};
  `;
});
const StyledHeaders = styled.div<{
  nested?: boolean;
  full_page: boolean;
  widths: ColumnWidth[];
}>(({
  theme,
  nested,
  full_page,
  widths
}) => {
  return css`
    border-bottom: ${theme.border};
    background-color: ${theme.colors.surface.base.variant_surface_3};
    display: grid;
    grid-template-columns: ${createGridTemplate(widths, full_page)};
    min-width: fit-content;
    ${full_page && css`
      bottom: ${theme.pxToRem(pagination_height)};
    `}

    ${nested && css`
      ${StyledCell}:first-child {
        padding-right: ${theme.spacing(6)};
      }
      position: relative;
      top: unset;
      bottom: unset;
      z-index: 2;
      &:before {
        content: '';
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: 0;
        z-index: 6;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
  `;
});
const StyledCell = styled.div<{
  header: boolean;
  full_page: boolean;
  list?: boolean;
  selected?: boolean;
  draggable?: boolean;
  $is_first?: boolean;
  $horizontal_offset?: number;
  nested?: boolean;
  has_scrolled: boolean;
}>(({
  theme,
  header,
  list,
  full_page,
  draggable,
  $is_first,
  nested,
  selected,
  $horizontal_offset = 0,
  has_scrolled
}) => css`
    height: 20px;
    display: flex;
    align-items: center;
    min-width: 0;
    ${draggable && header && css`
      position: relative;
      padding: 0;
      /* Add padding to height since it's now inside the absolutely positioned wrapper */
      height: calc(20px + ${theme.spacing(2)});
    `}

    ${!draggable && css`
      padding: ${theme.spacing(header ? 1 : list ? 3 : 2)} ${theme.spacing(2)};
      ${full_page && css`
        &:first-child {
          padding-left: ${theme.spacing(6)};
        }
        &:last-child {
          padding-right: ${theme.spacing(6)};
        }
      `}
    `}
    ${!list && css`
      &:not(:last-child) {
        border-right: ${({
  theme
}) => theme.border};
      }
    `}
    ${$is_first && full_page && css`
      position: sticky;
      left: 0;
      z-index: ${header ? 5 : 3};
      background-color: ${theme.colors.surface.base.variant_surface_3};
      ${!header && css`
        background-color: inherit;
      `}

      ${has_scrolled && css`
        &:after {
          content: '';
          position: absolute;
          top: 0;
          bottom: -1px;
          right: 0;
          width: 2px;
          z-index: 4;
          background-color: transparent;
          box-shadow:
            2px 0 4px 0 rgba(0, 0, 0, 0.08),
            4px 0 8px 0 rgba(0, 0, 0, 0.08);
          pointer-events: none;
        }
      `}
    `}

    ${$is_first && (nested || selected) && full_page && css`
      ${StyledCell}:first-child {
        padding-right: ${theme.spacing(6)};
      }
      position: sticky;
      left: 0;
      z-index: ${header ? 5 : 3};
      background-color: ${theme.colors.surface.base.variant_surface_3};
      ${!header && css`
        background-color: inherit;
      `}
      &:before {
        content: '';
        position: absolute;
        top: 0px;
        bottom: -1px;
        left: 0;
        z-index: 6;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
    ${!$is_first && header && full_page && $horizontal_offset > 0 && css`
      transform: translateX(-${$horizontal_offset}px);
    `}
  `);
const StyledTable = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: ${({
  theme
}) => theme.colors.surface.base.surface};
  height: 100%;
`;
const StyledField = styled.div<{
  cursor?: CSSProperties['cursor'];
}>(({
  cursor
}) => css`
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: center;
    text-align: left;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    border: 0;
    background-color: transparent;
    outline: none;
    box-shadow: none;
    cursor: ${cursor || 'pointer'};

    &:disabled {
      color: unset;
      cursor: default;
    }
  `);

// Helper function to convert widths array to grid-template-columns
const createGridTemplate = (widths: ColumnWidth[], full_page: boolean): string => {
  return widths.map((width, i) => {
    // Handle undefined or null widths
    if (!width) {
      return '1fr';
    }
    if (typeof width === 'number') {
      return `${width * 100}%`;
    } else {
      let {
        min,
        max
      } = width;
      const type = width.type || 'px';
      const unit = type === 'percentage' ? '%' : 'px';
      if (unit === 'px') {
        if (min) {
          min = min + 16;
          if ((i === 0 || i === widths.length - 1) && full_page) {
            min = min + 16;
          }
        }
        if (max) {
          max = max + 16;
          if ((i === 0 || i === widths.length - 1) && full_page) {
            max = max + 16;
          }
        }
      }
      if (min && max) {
        return `minmax(${min}${unit}, ${max}${unit})`;
      } else if (min) {
        return `minmax(${min}${unit}, 1fr)`;
      } else if (max) {
        return `minmax(0, ${max}${unit})`;
      } else {
        return '1fr';
      }
    }
  }).join(' ');
};
type Pagination = {
  current_page: number;
  page_size: number;
  entity_name: string;
};
const StyledSelectableRow = styled.div<{
  selected?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  selectable?: boolean;
  full_page?: boolean;
  nested?: boolean;
  $widths: ColumnWidth[];
  top_offset?: number;
  rows_length?: number;
  pagination?: Pagination;
}>(({
  theme,
  selected,
  selectable,
  highlighted,
  outdated,
  full_page,
  nested,
  $widths,
  rows_length,
  pagination
}) => css`
    position: relative;
    overflow: visible;
    display: grid;
    grid-template-columns: ${createGridTemplate($widths, full_page || false)};
    background-color: ${theme.colors.surface.base.surface};
    min-width: fit-content;

    ${!selected && css`
      border-bottom: ${theme.border};

      // If it's the last row of a full page table that has as many rows as the page size, don't show the bottom border as it will make a double border.
      ${full_page && rows_length === pagination?.page_size && css`
        &:last-child {
          border-bottom: none;
        }
      `}
    `}
    &:last-child {
      // Display hack to hide the border on the last row when next to the footer
      transform: translateY(1px);
      margin-top: -1px;
    }
    ${selectable && css`
      cursor: pointer;
      &:hover {
        &:not(:has(button:hover, a:hover)) {
          background-color: ${theme.colors.surface.base.hover.neutral};
        }
      }
    `}
    ${outdated && !selected && css`
      opacity: 0.6;
    `}
    ${highlighted && css`
      &:before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: 2;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
    ${selected && css`
      z-index: 3;
      margin-bottom: 1px;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        bottom: -1px;
        right: 0;
        left: 0;
        z-index: ${theme.zindex.fixed};
        border: 2px solid ${theme.colors.outline.focus.primary};
        pointer-events: none;
      }
    `}
  `);
const StyledLoadingOverlay = styled.div<{
  full_page: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({
  theme
}) => theme.colors.surface.base.surface};
  opacity: 0.8;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;
const StyledLoadPromptWrapper = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  z-index: 1;
  background-color: var(--color-background);
`;
const StyledTableBar = styled(Div)<{
  is_loading?: boolean;
  sticky?: boolean;
  primary?: boolean;
  $clickable?: boolean;
  $top?: boolean;
  $full_page?: boolean;
}>(({
  theme,
  is_loading,
  sticky,
  primary,
  $clickable,
  $top,
  $full_page
}) => css`
    ${$top ? css`
          border-bottom: ${theme.border};
        ` : css`
          border-top: ${theme.border};
        `}

    ${primary && css`
      ${Text} {
        color: ${theme.colors.on.neutral.primary};
      }
      ${StyledIcon} {
        color: ${theme.colors.on.neutral.primary};
      }
    `}

    ${$clickable && css`
      cursor: pointer;
      &:hover {
        background-color: ${theme.colors.surface.base.hover.neutral};
      }
    `}

    ${sticky && !$top && css`
      position: sticky;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 5;
      isolation: isolate;
    `}

    ${sticky && $top && css`
      position: sticky;
      left: 0;
      z-index: 3;
      ${$full_page && css`
        width: calc(100vw - ${DASHBOARD_NAV_WIDTH}px);
      `}
    `}

    ${is_loading ? css`
          background: repeating-linear-gradient(
            45deg,
            ${theme.colors.surface.base.variant_surface_2},
            ${theme.colors.surface.base.variant_surface_2} 16px,
            ${theme.colors.surface.base.variant_surface_3} 16px,
            ${theme.colors.surface.base.variant_surface_3} 32px
          );
        ` : css`
          background: ${theme.colors.surface.base.variant_surface_3};
        `}
  `);
export const StyledLoadProgressBar = styled(Div)<{
  value: number;
}>(({
  theme,
  value
}) => css`
    height: 2px;
    width: 100%;
    border-radius: ${theme.radius.large};
    background-color: ${theme.colors.surface.container.neutral};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      width: ${value}%;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background: linear-gradient(90deg, rgba(0, 68, 204, 0.5) 0%, #04c 100%);
      border-radius: ${theme.radius.large};
      transition: width 0.25s linear;
    }
  `);
export const TableBar: React.FC<{
  is_loading?: boolean;
  load_progress?: number;
  sticky?: boolean;
  children?: React.ReactNode;
  primary?: boolean;
  onClick?: () => void;
  top?: boolean;
  full_page?: boolean;
}> = t0 => {
  const $ = _c(13);
  const {
    is_loading,
    load_progress,
    sticky,
    children,
    primary,
    onClick,
    top,
    full_page
  } = t0;
  const t1 = !!onClick;
  let t2;
  if ($[0] !== is_loading || $[1] !== load_progress) {
    t2 = (is_loading || load_progress !== undefined) && <StyledLoadProgressBar value={load_progress ?? 0} />;
    $[0] = is_loading;
    $[1] = load_progress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== full_page || $[5] !== is_loading || $[6] !== onClick || $[7] !== primary || $[8] !== sticky || $[9] !== t1 || $[10] !== t2 || $[11] !== top) {
    t3 = <StyledTableBar is_loading={is_loading} sticky={sticky} primary={primary} $clickable={t1} $top={top} $full_page={full_page} onClick={onClick}>{children}{t2}</StyledTableBar>;
    $[3] = children;
    $[4] = full_page;
    $[5] = is_loading;
    $[6] = onClick;
    $[7] = primary;
    $[8] = sticky;
    $[9] = t1;
    $[10] = t2;
    $[11] = top;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
type CustomField = {
  element: JSX.Element;
  clickable_area: boolean;
};
type Field = string | number | boolean | JSX.Element | CustomField | null;
interface Row {
  id: string | number;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  fields?: Field[];
  widths?: ColumnWidth[];
  element?: React.ReactNode;
  has_error?: boolean;
  expendable?: boolean;
}
interface NestedTable {
  headers: Header[];
  rows: Row[];
}
type Header = string | JSX.Element;
type onRowSelected = (row_id: string | undefined) => void;
interface Props {
  pagination?: Pagination;
  list?: boolean;
  fill?: boolean;
  empty_results_label?: string;
  raised?: boolean;
  footer?: boolean;
  headers: Header[];
  top_offset?: number;
  rows: (Row | NestedTable)[];
  widths: ColumnWidth[];
  current_count?: number;
  alert_top?: string;
  bottom_element?: React.ReactNode;
  sticky_bottom_element?: React.ReactNode;
  loading?: boolean;
  loading_title?: string;
  loading_message?: string;
  total_count?: number | null;
  draggable_columns?: boolean;
  draggable_column_indices?: number[];
  onNextPage?(): void;
  onPreviousPage?(): void;
  onRowSelected?: onRowSelected;
  onColumnReorder?(active_index: number, over_index: number): void;
  onColumnResize?(column_index: number, new_stored_width: number): void;
  table_body_h?: StyledUtilsProps['h'];
  table_body_style?: React.CSSProperties;
  has_new_results?: boolean;
  onRefresh?: () => void;
}
const TableField: React.FC<{
  field: Field;
}> = t0 => {
  const $ = _c(8);
  const {
    field
  } = t0;
  if (typeof field === "string" || typeof field === "number" || typeof field === "boolean") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        overflow: "hidden"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== field) {
      t2 = <StyledField style={t1}><Text ellipsis={true} size="s">{field}</Text></StyledField>;
      $[1] = field;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (field === null || field === undefined) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <StyledField><Text muted={true} size="s">–</Text></StyledField>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (typeof field === "object" && (field as any).element) {
    const t1 = field as CustomField;
    let t2;
    if ($[4] !== t1.element) {
      t2 = <StyledField>{t1.element}</StyledField>;
      $[4] = t1.element;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  const t1 = field as any;
  let t2;
  if ($[6] !== t1) {
    t2 = <StyledField>{t1}</StyledField>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const TableRow: React.FC<{
  row: Row;
  widths: ColumnWidth[];
  onRowSelected?: onRowSelected;
  nested: boolean;
  list?: boolean;
  full_page: boolean;
  top_offset?: number;
  rows_length?: number;
  pagination?: Pagination;
}> = t0 => {
  const $ = _c(29);
  const {
    row,
    full_page,
    widths,
    onRowSelected,
    nested,
    list,
    top_offset,
    rows_length,
    pagination
  } = t0;
  const selectable = row.selectable === false ? false : !!onRowSelected;
  let icon = row.highlighted ? "expand_more" : "chevron_right";
  const table_context = useContext(TableContext);
  const has_scrolled = table_context?.has_scrolled || false;
  if (row.outdated) {
    icon = "visibility_off";
  }
  const t1 = !nested && row.outdated;
  let t2;
  if ($[0] !== full_page || $[1] !== has_scrolled || $[2] !== icon || $[3] !== list || $[4] !== nested || $[5] !== onRowSelected || $[6] !== row.element || $[7] !== row.expendable || $[8] !== row.fields || $[9] !== row.has_error || $[10] !== row.highlighted || $[11] !== row.id || $[12] !== row.outdated || $[13] !== row.selected || $[14] !== selectable) {
    t2 = row.element ? row.element : row.fields?.map((field, i) => <StyledCell key={i} list={list} header={false} selected={row.selected} full_page={full_page} nested={nested} $is_first={i === 0} has_scrolled={has_scrolled} onClick={() => selectable && !!onRowSelected && onRowSelected(row.id as string)}><Div h={{
        px: 20
      }}>{onRowSelected && i === 0 && !nested && (row.expendable ? row.has_error ? <Div m={{
          l: -1.5,
          y: -1.5,
          r: 1.5
        }} p={1.5}><Icon muted={row.outdated} danger={!row.outdated} icon={row.outdated ? "visibility_off" : "error"} /></Div> : <Tooltip disabled={!row.outdated} side="bottom" tooltip="This row no longer matches your current filter." offset={[-6, 6]}><Button m={{
            l: -1,
            r: 1.5
          }} ghost={true} small={true} icon={icon} onClick={e => {
            e.stopPropagation();
            row.highlighted ? onRowSelected(undefined) : onRowSelected(row.id as string);
          }} /></Tooltip> : null)}{onRowSelected && i === 0 && nested && <Icon icon="subdirectory" small={true} right={-0.5} left={2} />}</Div><TableField field={field} /></StyledCell>);
    $[0] = full_page;
    $[1] = has_scrolled;
    $[2] = icon;
    $[3] = list;
    $[4] = nested;
    $[5] = onRowSelected;
    $[6] = row.element;
    $[7] = row.expendable;
    $[8] = row.fields;
    $[9] = row.has_error;
    $[10] = row.highlighted;
    $[11] = row.id;
    $[12] = row.outdated;
    $[13] = row.selected;
    $[14] = selectable;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== full_page || $[17] !== nested || $[18] !== pagination || $[19] !== row.highlighted || $[20] !== row.id || $[21] !== row.selected || $[22] !== rows_length || $[23] !== selectable || $[24] !== t1 || $[25] !== t2 || $[26] !== top_offset || $[27] !== widths) {
    t3 = <StyledSelectableRow rows_length={rows_length} pagination={pagination} key={row.id} selectable={selectable} highlighted={row.highlighted} outdated={t1} selected={row.selected} nested={nested} $widths={widths} top_offset={top_offset} full_page={full_page}>{t2}</StyledSelectableRow>;
    $[16] = full_page;
    $[17] = nested;
    $[18] = pagination;
    $[19] = row.highlighted;
    $[20] = row.id;
    $[21] = row.selected;
    $[22] = rows_length;
    $[23] = selectable;
    $[24] = t1;
    $[25] = t2;
    $[26] = top_offset;
    $[27] = widths;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  return t3;
};
const Table: React.FC<Props> = t0 => {
  const $ = _c(116);
  const {
    headers,
    rows,
    list,
    widths,
    onRowSelected,
    current_count,
    onNextPage,
    onPreviousPage,
    pagination,
    fill,
    empty_results_label,
    footer: t1,
    top_offset: t2,
    alert_top,
    bottom_element,
    sticky_bottom_element,
    loading,
    loading_title,
    loading_message,
    total_count,
    draggable_columns: t3,
    draggable_column_indices: t4,
    onColumnReorder,
    onColumnResize,
    table_body_h,
    table_body_style,
    onRefresh,
    has_new_results
  } = t0;
  const footer = t1 === undefined ? true : t1;
  const top_offset = t2 === undefined ? 0 : t2;
  const draggable_columns = t3 === undefined ? false : t3;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const draggable_column_indices = t5;
  const [cached_rows, setCachedRows] = useState(rows);
  const [cached_count, setCachedCount] = useState(current_count);
  const [active_drag_id, setActiveDragId] = useState(null);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Map();
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const [resize_width_overrides, setResizeWidthOverrides] = useState(t6);
  const [is_any_resizing, setIsAnyResizing] = useState(false);
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const table_context = useContext(TableContext);
  const has_scrolled = table_context?.has_scrolled || false;
  let t7;
  bb0: {
    if (resize_width_overrides.size === 0) {
      t7 = widths;
      break bb0;
    }
    let t8;
    if ($[3] !== resize_width_overrides || $[4] !== widths) {
      let t9;
      if ($[6] !== resize_width_overrides) {
        t9 = (w, i) => {
          const override = resize_width_overrides.get(i);
          if (override !== undefined) {
            return {
              min: override,
              max: override
            };
          }
          return w;
        };
        $[6] = resize_width_overrides;
        $[7] = t9;
      } else {
        t9 = $[7];
      }
      t8 = widths.map(t9);
      $[3] = resize_width_overrides;
      $[4] = widths;
      $[5] = t8;
    } else {
      t8 = $[5];
    }
    t7 = t8;
  }
  const effective_widths = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = column_index => {
      setIsAnyResizing(true);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleResizeStart = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (column_index_0, new_width) => {
      setResizeWidthOverrides(prev => {
        const next = new Map(prev);
        next.set(column_index_0, new_width);
        return next;
      });
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const handleResize = t9;
  let t10;
  if ($[10] !== onColumnResize) {
    t10 = (column_index_1, new_width_0) => {
      setResizeWidthOverrides(new Map());
      setIsAnyResizing(false);
      onColumnResize?.(column_index_1, new_width_0);
    };
    $[10] = onColumnResize;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleResizeEnd = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const sensors = useSensors(useSensor(PointerSensor, t11), useSensor(KeyboardSensor));
  let t12;
  let t13;
  if ($[13] !== loading || $[14] !== rows) {
    t12 = () => {
      if (!loading) {
        setCachedRows(rows);
      } else {
        setCachedRows([]);
      }
    };
    t13 = [rows, loading];
    $[13] = loading;
    $[14] = rows;
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[17] !== current_count || $[18] !== loading) {
    t14 = () => {
      if (!loading) {
        setCachedCount(current_count);
      }
    };
    t15 = [current_count, loading];
    $[17] = current_count;
    $[18] = loading;
    $[19] = t14;
    $[20] = t15;
  } else {
    t14 = $[19];
    t15 = $[20];
  }
  useEffect(t14, t15);
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event => {
      setActiveDragId(event.active.id as string);
    };
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const handleDragStart = t16;
  let t17;
  if ($[22] !== draggable_column_indices || $[23] !== headers || $[24] !== onColumnReorder) {
    t17 = event_0 => {
      const {
        active,
        over
      } = event_0;
      setActiveDragId(null);
      if (over && active.id !== over.id && onColumnReorder) {
        const active_index = headers.findIndex((_, index) => `header-${index}` === active.id);
        const over_index = headers.findIndex((__0, index_0) => `header-${index_0}` === over.id);
        const is_active_draggable = draggable_column_indices.includes(active_index);
        const is_over_draggable = draggable_column_indices.includes(over_index);
        if (active_index !== -1 && over_index !== -1 && is_active_draggable && is_over_draggable) {
          onColumnReorder(active_index, over_index);
        }
      }
    };
    $[22] = draggable_column_indices;
    $[23] = headers;
    $[24] = onColumnReorder;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const handleDragEnd = t17;
  let t18;
  if ($[26] !== draggable_column_indices || $[27] !== draggable_columns || $[28] !== fill || $[29] !== handleResizeEnd || $[30] !== has_scrolled || $[31] !== headers || $[32] !== is_any_resizing || $[33] !== list || $[34] !== widths) {
    t18 = () => {
      const is_column_draggable = index_1 => draggable_columns && draggable_column_indices.includes(index_1);
      return headers.map((header, i_0) => {
        const width_config = widths[i_0];
        let configured_width;
        let stored_width;
        if (typeof width_config === "object" && width_config.min === width_config.max) {
          stored_width = width_config.min;
          configured_width = width_config.min;
          if (configured_width) {
            configured_width = configured_width + 16;
            if ((i_0 === 0 || i_0 === headers.length - 1) && (fill || false)) {
              configured_width = configured_width + 16;
            }
          }
        }
        const is_resizable = is_column_draggable(i_0) && stored_width !== undefined;
        return <StyledCell key={i_0} header={true} full_page={fill || false} list={list} nested={false} draggable={is_column_draggable(i_0)} $is_first={i_0 === 0} has_scrolled={has_scrolled}>{is_column_draggable(i_0) ? <DraggableColumnHeader id={`header-${i_0}`} full_page={fill || false} is_first={i_0 === 0} is_last={i_0 === headers.length - 1} configured_width={configured_width} resizable={is_resizable} column_index={i_0} stored_width={stored_width} is_any_resizing={is_any_resizing} onResizeStart={handleResizeStart} onResize={handleResize} onResizeEnd={handleResizeEnd}>{typeof header === "string" ? <StyledField><Text semi={true} muted={true} size="xs" as="span" text_wrap={false}>{header}</Text></StyledField> : <StyledField>{header}</StyledField>}</DraggableColumnHeader> : typeof header === "string" ? <StyledField cursor="default"><Text semi={true} muted={true} size="xs" as="span" text_wrap={false}>{header}</Text></StyledField> : <StyledField>{header}</StyledField>}</StyledCell>;
      });
    };
    $[26] = draggable_column_indices;
    $[27] = draggable_columns;
    $[28] = fill;
    $[29] = handleResizeEnd;
    $[30] = has_scrolled;
    $[31] = headers;
    $[32] = is_any_resizing;
    $[33] = list;
    $[34] = widths;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const renderHeaders = t18;
  const table_width = `calc(100vw - ${DASHBOARD_NAV_WIDTH}px)`;
  let t19;
  if ($[36] !== draggable_column_indices || $[37] !== draggable_columns) {
    t19 = draggable_columns ? draggable_column_indices.map(_temp) : [];
    $[36] = draggable_column_indices;
    $[37] = draggable_columns;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const sortable_items = t19;
  let t20;
  if ($[39] !== active_drag_id || $[40] !== effective_widths || $[41] !== fill || $[42] !== handleDragEnd || $[43] !== headers || $[44] !== list || $[45] !== renderHeaders || $[46] !== rows.length || $[47] !== sensors || $[48] !== sortable_items || $[49] !== top_offset || $[50] !== widths) {
    t20 = rows.length > 0 && headers?.length > 0 && <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}><StyledHeaderWrapper full_page={fill || false} top_offset={top_offset}><StyledHeaders full_page={fill || false} widths={effective_widths}><SortableContext items={sortable_items} strategy={horizontalListSortingStrategy}>{renderHeaders()}</SortableContext></StyledHeaders></StyledHeaderWrapper><DragOverlay>{active_drag_id ? (() => {
          const active_header_index = headers.findIndex((__1, index_2) => `header-${index_2}` === active_drag_id);
          const active_header = headers[active_header_index];
          const width_config_0 = widths[active_header_index];
          let configured_width_0;
          if (typeof width_config_0 === "object" && width_config_0.min === width_config_0.max) {
            configured_width_0 = width_config_0.min;
            if (configured_width_0) {
              configured_width_0 = configured_width_0 + 16;
              if ((active_header_index === 0 || active_header_index === headers.length - 1) && (fill || false)) {
                configured_width_0 = configured_width_0 + 16;
              }
            }
          }
          return <StyledCell header={true} full_page={fill || false} list={list} nested={false} draggable={true} $is_first={active_header_index === 0} style={{
            width: configured_width_0 ? `${configured_width_0}px` : "auto",
            cursor: "grabbing",
            opacity: 0.9,
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)"
          }}><DraggableColumnHeader id={`overlay-${active_drag_id}`} disabled={true} full_page={fill || false} is_first={active_header_index === 0} is_last={active_header_index === headers.length - 1} configured_width={configured_width_0}>{typeof active_header === "string" ? <StyledField><Text semi={true} muted={true} size="xs" as="span" text_wrap={false}>{active_header}</Text></StyledField> : <StyledField>{active_header}</StyledField>}</DraggableColumnHeader></StyledCell>;
        })() : null}</DragOverlay></DndContext>;
    $[39] = active_drag_id;
    $[40] = effective_widths;
    $[41] = fill;
    $[42] = handleDragEnd;
    $[43] = headers;
    $[44] = list;
    $[45] = renderHeaders;
    $[46] = rows.length;
    $[47] = sensors;
    $[48] = sortable_items;
    $[49] = top_offset;
    $[50] = widths;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== alert_top) {
    t21 = alert_top && <><Alert warning={true} no_border={true}>{alert_top}</Alert><Divider /></>;
    $[52] = alert_top;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      direction: "column",
      grow: true
    };
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== table_body_style) {
    t23 = {
      minWidth: "fit-content",
      ...table_body_style
    };
    $[55] = table_body_style;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== fill || $[58] !== has_new_results || $[59] !== onRefresh) {
    t24 = has_new_results && onRefresh && <TableBar primary={true} top={true} sticky={true} full_page={fill || false} onClick={() => onRefresh()}><Div flex={{
        align: "center",
        justify: "center",
        gap: 1
      }} p={{
        x: 6,
        y: 1
      }}><Icon icon="autorenew" small={true} /><Text semi={true} size="s">Load newest...</Text></Div></TableBar>;
    $[57] = fill;
    $[58] = has_new_results;
    $[59] = onRefresh;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== cached_rows.length || $[62] !== fill || $[63] !== loading || $[64] !== loading_message || $[65] !== loading_title) {
    t25 = loading ? cached_rows.length !== 0 ? <StyledLoadPromptWrapper style={{
      width: table_width
    }}><StyledLoadingOverlay full_page={fill || false}><Loading /></StyledLoadingOverlay></StyledLoadPromptWrapper> : <Div p={{
      y: 12
    }} flex={{
      direction: "column",
      grow: true,
      justify: "center"
    }}><Loading loading_title={loading_title} loading_message={loading_message} /></Div> : null;
    $[61] = cached_rows.length;
    $[62] = fill;
    $[63] = loading;
    $[64] = loading_message;
    $[65] = loading_title;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== empty_results_label || $[68] !== fill || $[69] !== loading || $[70] !== pagination?.entity_name || $[71] !== rows.length) {
    t26 = !loading && rows.length === 0 && <>{fill ? <StyledEmptyStateWrapper><EmptyResults label={empty_results_label || `There is no ${pagination?.entity_name || "data"} to show`} /></StyledEmptyStateWrapper> : <EmptyResults label={empty_results_label || `There is no ${pagination?.entity_name || "data"} to show`} />}</>;
    $[67] = empty_results_label;
    $[68] = fill;
    $[69] = loading;
    $[70] = pagination?.entity_name;
    $[71] = rows.length;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== cached_rows || $[74] !== effective_widths || $[75] !== fill || $[76] !== is_degraded_banner_active || $[77] !== list || $[78] !== onRowSelected || $[79] !== pagination || $[80] !== top_offset) {
    let t28;
    if ($[82] !== cached_rows.length || $[83] !== effective_widths || $[84] !== fill || $[85] !== is_degraded_banner_active || $[86] !== list || $[87] !== onRowSelected || $[88] !== pagination || $[89] !== top_offset) {
      t28 = row => "headers" in row ? <StyledNestedBlock key="nested" full_page={fill || false} top_offset={top_offset + (is_degraded_banner_active ? 32 : 0)} nested={true}><StyledHeaderWrapper full_page={fill || false} nested={true} top_offset={0}><StyledHeaders nested={true} full_page={fill || false} widths={effective_widths}>{row.headers.map((header_0, i_2) => <StyledCell key={i_2} header={true} nested={true} full_page={fill || false} list={list} $is_first={i_2 === 0}>{typeof header_0 === "string" ? <StyledField as="div"><Text semi={true} muted={true} size="xs" as="span" text_wrap={false}>{header_0}</Text></StyledField> : <StyledField as="div">{header_0}</StyledField>}</StyledCell>)}</StyledHeaders></StyledHeaderWrapper>{row.rows.map(row_0 => <TableRow nested={true} list={list} key={row_0.id} row={row_0} widths={row_0.widths ?? effective_widths} onRowSelected={onRowSelected} full_page={fill || false} rows_length={cached_rows.length} pagination={pagination} />)}</StyledNestedBlock> : <TableRow nested={false} list={list} key={row.id} row={row} widths={row.widths ?? effective_widths} onRowSelected={onRowSelected} full_page={fill || false} top_offset={calculate_sticky_offset.rowWithinTable(top_offset) + (is_degraded_banner_active ? 32 : 0)} rows_length={cached_rows.length} pagination={pagination} />;
      $[82] = cached_rows.length;
      $[83] = effective_widths;
      $[84] = fill;
      $[85] = is_degraded_banner_active;
      $[86] = list;
      $[87] = onRowSelected;
      $[88] = pagination;
      $[89] = top_offset;
      $[90] = t28;
    } else {
      t28 = $[90];
    }
    t27 = cached_rows.map(t28);
    $[73] = cached_rows;
    $[74] = effective_widths;
    $[75] = fill;
    $[76] = is_degraded_banner_active;
    $[77] = list;
    $[78] = onRowSelected;
    $[79] = pagination;
    $[80] = top_offset;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[91] !== bottom_element || $[92] !== sticky_bottom_element) {
    t28 = sticky_bottom_element ? sticky_bottom_element : bottom_element && <StyledLoadPromptWrapper>{bottom_element}</StyledLoadPromptWrapper>;
    $[91] = bottom_element;
    $[92] = sticky_bottom_element;
    $[93] = t28;
  } else {
    t28 = $[93];
  }
  let t29;
  if ($[94] !== t23 || $[95] !== t24 || $[96] !== t25 || $[97] !== t26 || $[98] !== t27 || $[99] !== t28 || $[100] !== table_body_h) {
    t29 = <Div flex={t22} h={table_body_h} style={t23}>{t24}{t25}{t26}{t27}{t28}</Div>;
    $[94] = t23;
    $[95] = t24;
    $[96] = t25;
    $[97] = t26;
    $[98] = t27;
    $[99] = t28;
    $[100] = table_body_h;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  let t30;
  if ($[102] !== cached_count || $[103] !== fill || $[104] !== footer || $[105] !== loading || $[106] !== onNextPage || $[107] !== onPreviousPage || $[108] !== pagination || $[109] !== total_count) {
    t30 = footer && (cached_count && cached_count >= 0 || onPreviousPage || onNextPage) && <TableFooter fill={fill} current_count={cached_count} total_count={total_count} loading={loading} pagination={pagination} onPreviousPage={onPreviousPage} onNextPage={onNextPage} />;
    $[102] = cached_count;
    $[103] = fill;
    $[104] = footer;
    $[105] = loading;
    $[106] = onNextPage;
    $[107] = onPreviousPage;
    $[108] = pagination;
    $[109] = total_count;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] !== t20 || $[112] !== t21 || $[113] !== t29 || $[114] !== t30) {
    t31 = <StyledTable>{t20}{t21}{t29}{t30}</StyledTable>;
    $[111] = t20;
    $[112] = t21;
    $[113] = t29;
    $[114] = t30;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  return t31;
};
export default Table;
function _temp(i_1) {
  return `header-${i_1}`;
}